import {
  connect,
  on as onNetwork,
  sendPing,
  setLastSnapshotTick
} from '../network-client.js';
import {
  PING_INTERVAL_MS,
  PING_OUTSTANDING_LIMIT,
  PING_SAMPLE_MAX_AGE_MS
} from '../engine/config.js';
import { model, authoritative, ui, net, lobby, lobbyRuntime, authState } from '../engine/state.js';
import { syncServerClock, serverNowMs } from '../engine/time.js';
import { toSnapshotStruct } from '../utils/utils.js';
import { sameHostName } from '../engine/players.js';
import { setStatus } from '../ui/status.js';
import { clearSession, readSession, storeSession } from './session.js';
import { prunePingRecords } from './ping-tracker.js';
import { resetRemoteMotion } from '../engine/prediction.js';
import { applyOps, setCanvasSize, fitStageToViewport, clearField } from '../render/render.js';
import { clearScheduledStartSfx } from '../audio/audio.js';
import {
  resetChatFeed,
  appendChatEntry,
  setChatAdmin,
  showChatNotice,
  chatBlockedNoticeText,
  removeChatLineById,
  clearChatMessages
} from '../ui/chat.js';
import {
  applyRoomState,
  fetchRoomDirectory,
  handleColorTaken,
  handleTrailOpacityChanged,
  handleTurnRadiusChanged,
  applyOwnTurnRadiusPercent,
  lobbyPlayersNeeded,
  isLobbyVisible,
  openRoomPasswordPrompt,
  renderLobby,
  sanitizeStartMode,
  setLobbyCopyButtonLabel,
  setLobbyVisible,
  syncLobbyCountdownFromState,
  countdownGoHoldRemainingMs
} from '../ui/lobby.js';

// Dev-only replay capture registers itself here; production never loads it.
let replayFrameRecorder = null;

function setReplayFrameRecorder(fn) {
  replayFrameRecorder = typeof fn === 'function' ? fn : null;
}

const networkDeps = {
  applyState: () => {},
  pushSnapshot: () => {},
  updatePredictionAnchor: () => {},
  applyAckSeq: () => {},
  sendCurrentInput: () => {},
  inputHeartbeatIntervalMs: () => 100,
  clearAllInputRetries: () => {},
  clearInput: () => {}
};

function setNetworkDeps(deps = {}) {
  Object.assign(networkDeps, deps);
}

const RECONNECT_MAX_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 8000;

// Auto-reconnect with a stored session token. Suppressed after a takeover or
// room-full so two tabs never fight over one session.
const reconnectState = {
  attempts: 0,
  timer: 0,
  suppressed: false,
  fallbackMessage: ''
};

function cancelReconnect() {
  if (reconnectState.timer) {
    clearTimeout(reconnectState.timer);
    reconnectState.timer = 0;
  }
  reconnectState.attempts = 0;
}

function suppressReconnect(fallbackMessage = '') {
  reconnectState.suppressed = true;
  if (fallbackMessage) {
    reconnectState.fallbackMessage = fallbackMessage;
  }
  if (reconnectState.timer) {
    clearTimeout(reconnectState.timer);
    reconnectState.timer = 0;
  }
}

let heartbeatHandle = 0;
let pingHandle = 0;

// Snapshots omit powerups and static player fields (label/color/isBot) when
// unchanged; rebuild full states from this cache before anything consumes them.
const snapshotCache = {
  powerups: [],
  playerMetaById: new Map(),
  playerSpawnById: new Map(),
  playerFlagsById: new Map()
};

const SPAWN_FIELDS = [
  'respawnAtMs',
  'spawnAtMs',
  'spawnDelayMs',
  'spawnScaleStart',
  'spawnMarkerUntilMs'
];

// alive/connected/drawingTrail, sent as one block whenever any of them moves.
// Grouped on the server too, so an absent block always means all three are
// unchanged and there is never a partial update to reconcile.
const FLAG_FIELDS = ['alive', 'connected', 'drawingTrail'];

function resetSnapshotCache() {
  snapshotCache.powerups = [];
  snapshotCache.playerMetaById.clear();
  snapshotCache.playerSpawnById.clear();
  snapshotCache.playerFlagsById.clear();
}

function hydrateSnapshotState(state) {
  if (Array.isArray(state.players)) {
    for (const player of state.players) {
      if (!player || !Number.isFinite(Number(player.id))) continue;
      const id = Number(player.id);
      if (typeof player.label === 'string' || player.color !== undefined) {
        snapshotCache.playerMetaById.set(id, {
          label: player.label,
          color: player.color,
          isBot: player.isBot,
          // Which side they are on, cached for the same reason as the mesh: it
          // rides the meta snapshots only, and the head shape is drawn from it
          // every frame -- uncached, a team's heads would go round again the
          // moment the metadata stopped repeating.
          team: player.team ?? null,
          // Mesh players carry gradient stops alongside their flat color. Meta
          // rides only the snapshots that change it, so without caching it here
          // the stops survive exactly one frame and the trail falls back to
          // flat everywhere after that.
          mesh: player.mesh ?? null
        });
      } else {
        const meta = snapshotCache.playerMetaById.get(id);
        if (meta) {
          player.label = meta.label;
          player.color = meta.color;
          player.isBot = meta.isBot;
          player.team = meta.team;
          player.mesh = meta.mesh;
        }
      }

      // Fields the server omits at their default value, restored here so every
      // consumer downstream -- prediction, render, replay recording -- keeps
      // seeing the same complete player object it always has. spawnAtMs in
      // particular gates respawn detection in prediction.js behind an
      // isFinite() check, which an absent value would fail silently.
      if (player.spawnAtMs === undefined) {
        const spawn = snapshotCache.playerSpawnById.get(id);
        if (spawn) Object.assign(player, spawn);
      } else {
        const spawn = {};
        for (const field of SPAWN_FIELDS) spawn[field] = player[field];
        snapshotCache.playerSpawnById.set(id, spawn);
      }
      // Absent means unchanged, not default: `alive` in particular gates the
      // death overlay and prediction, so defaulting it would resurrect every
      // dead player on the first snapshot that omitted the block.
      if (player.alive === undefined) {
        const flags = snapshotCache.playerFlagsById.get(id);
        if (flags) Object.assign(player, flags);
      } else {
        const flags = {};
        for (const field of FLAG_FIELDS) flags[field] = player[field];
        snapshotCache.playerFlagsById.set(id, flags);
      }
      if (player.isFlying === undefined) player.isFlying = false;
      if (player.isGapping === undefined) player.isGapping = false;
      if (player.flyTicks === undefined) player.flyTicks = 0;
      if (player.effect === undefined) {
        player.effect = { type: null, ticks: 0, stacks: 0 };
      }
    }
  }
  if (Array.isArray(state.powerups)) {
    snapshotCache.powerups = state.powerups;
  } else {
    state.powerups = snapshotCache.powerups;
  }
}

function handleWelcome(message) {
  if (lobbyRuntime.returnToLobbyInProgress) return;
  resetSnapshotCache();
  net.playerNetworkStats.clear();
  cancelReconnect();
  reconnectState.suppressed = false;
  reconnectState.fallbackMessage = '';
  setChatAdmin(Boolean(message?.chatAdmin));
  if (typeof message?.name === 'string' && message.name.trim()) {
    // The server may have suffixed the display name to keep it unique.
    net.playerName = message.name.trim();
  }
  if (typeof message?.token === 'string' && message.token) {
    const roomIdForToken = (typeof message?.room?.id === 'string' && message.room.id.trim())
      ? message.room.id.trim()
      : net.roomId;
    storeSession(roomIdForToken, message.token, net.playerName);
  }
  const runningRoom = Number.isFinite(Number(message?.config?.fieldWidth))
    && Number.isFinite(Number(message?.config?.fieldHeight));
  if (message?.config && typeof message.config === 'object') {
    model.config = { ...model.config, ...message.config };
    setCanvasSize(model.config.fieldWidth, model.config.fieldHeight);
    fitStageToViewport();
    clearField();
  }
  if (message?.playerSettings && typeof message.playerSettings === 'object') {
    applyOwnTurnRadiusPercent(
      message.playerSettings.turnRadiusPercent,
      message.playerSettings.turnRadius
    );
  }
  if (Number.isFinite(Number(message?.playerId))) {
    net.localPlayerId = Number(message.playerId);
  }
  if (Number.isFinite(Number(message?.simTick))) {
    net.lastSnapshotTick = Number(message.simTick);
    setLastSnapshotTick(net.lastSnapshotTick);
  }
  if (Number.isFinite(Number(message?.serverNow))) {
    syncServerClock(Number(message.serverNow));
  }
  if (message?.room && typeof message.room === 'object') {
    lobby.mode = 'room';
    if (typeof message.room.id === 'string' && message.room.id.trim()) {
      lobby.roomId = message.room.id.trim();
      net.roomId = lobby.roomId;
    }
    if (typeof message.room.name === 'string' && message.room.name.trim()) {
      lobby.roomName = message.room.name.trim();
    }
    if (typeof message.room.hostName === 'string') {
      lobby.hostName = message.room.hostName.trim();
      lobby.isHost = sameHostName(lobby.hostName, net.playerName);
    }
    if (Number.isFinite(Number(message.room.maxPlayers))) {
      lobby.maxPlayers = Math.max(1, Math.floor(Number(message.room.maxPlayers)));
    }
    if (Number.isFinite(Number(message.room.minPlayersToStart))) {
      lobby.minPlayersToStart = Math.max(1, Math.floor(Number(message.room.minPlayersToStart)));
    }
    if (typeof message.room.startMode === 'string') {
      lobby.startMode = sanitizeStartMode(message.room.startMode, lobby.startMode || 'host');
    }
    renderLobby();
  }
  networkDeps.clearAllInputRetries();
  net.pendingInputs = [];
  net.confirmedInput = { left: false, right: false, seq: 0 };
  net.predictionBaseInput = { left: false, right: false };
  net.predictionInputHistory = [];
  net.predictedInput = { left: false, right: false };
  net.predictionAnchor = null;
  net.predictionDisplay = null;
  net.predictionCorrection = { x: 0, y: 0, angle: 0, lastAtMs: 0 };
  net.lastSentInputSig = '';
  net.lastInputSendClientMs = 0;
  net.lastAckSeq = null;
  net.snapshotBuffer = [];
  resetRemoteMotion();
  net.lastControlChangeClientMs = 0;
  clearScheduledStartSfx();
  ui.hasStateSnapshot = false;
  ui.overlayStateSig = '';
  ui.scoreSig = '';
  ui.standingsSig = '';
  ui.waitingRoundKey = '';
  ui.waitingOverlayNeedsAnimate = false;
  ui.scoreDirty = true;
  ui.overlayDirty = true;
  ui.controlsDirty = true;
  ui.lastOverlayCountdown = -1;
  authoritative.state = toSnapshotStruct(model.state, authoritative.state, { includeEvents: true });
  if (runningRoom) {
    lobby.canStart = true;
    lobby.phase = 'running';
    lobby.message = 'Match running...';
    // A fresh match welcome ends any waiting-room peek: pull the player into the
    // arena. Without this, the room_state that follows would re-open the waiting
    // room (its keep-arena check bails while peeking), leaving them stuck there
    // after the post-match countdown until they hit Resume.
    lobbyRuntime.matchLobbyPeek = false;
    // A match start sends every client a fresh welcome, which is what actually
    // pulls them into the arena. Respect the post-"Go!" hold here too, or the
    // scene switches straight past it; syncLobbyCountdownFromState runs every
    // frame and performs the hide once the hold expires.
    if (isLobbyVisible() && countdownGoHoldRemainingMs() <= 0) {
      setLobbyVisible(false);
    }
    setStatus(`Connected: room "${net.roomId}" as ${net.playerName}`);
    syncLobbyCountdownFromState(serverNowMs());
    return;
  }
  const waitingForPlayers = !lobby.canStart;
  if (waitingForPlayers) {
    lobby.phase = 'waiting';
    const needed = lobbyPlayersNeeded();
    lobby.message = needed > 0
      ? `Waiting for ${needed} more player${needed === 1 ? '' : 's'}...`
      : 'Waiting for match start...';
    setLobbyVisible(true);
    renderLobby();
    if (net.connected) {
      networkDeps.clearInput();
    }
  } else {
    lobby.phase = lobby.startMode === 'host' ? 'ready' : 'starting';
    lobby.message = lobby.phase === 'ready'
      ? (lobby.isHost ? 'Players ready. Start when ready.' : 'Waiting for host to start...')
      : 'Match starting...';
    setLobbyVisible(true);
    renderLobby();
    if (net.connected) {
      networkDeps.clearInput();
    }
  }
  syncLobbyCountdownFromState(serverNowMs());
}

function handleSnapshot(message) {
  if (!message || typeof message !== 'object') return;
  if (Number.isFinite(Number(message.serverNow))) {
    syncServerClock(Number(message.serverNow));
  }
  if (Number.isFinite(Number(message.simTick))) {
    net.lastSnapshotTick = Number(message.simTick);
    setLastSnapshotTick(net.lastSnapshotTick);
  }
  if (Number.isFinite(Number(message.ackInputSeq))) {
    networkDeps.applyAckSeq(message.ackInputSeq);
  }
  if (message.state && typeof message.state === 'object') {
    hydrateSnapshotState(message.state);
    networkDeps.applyState(message.state);
    networkDeps.pushSnapshot(message.state, message.simTick, message.serverNow);
    networkDeps.updatePredictionAnchor(message.state);
  }
  applyOps(message.ops || []);
  if (replayFrameRecorder && message.state && typeof message.state === 'object') {
    replayFrameRecorder(message.state, message.ops || [], {
      simTick: message.simTick,
      serverNow: message.serverNow
    });
  }
  net.lastSnapshotAtMs = Date.now();
  syncLobbyCountdownFromState(serverNowMs());
}

function handleInputAck(message) {
  if (!message || typeof message !== 'object') return;
  if (Number.isFinite(Number(message.serverNow))) {
    syncServerClock(Number(message.serverNow));
  }
  if (!Number.isFinite(Number(message.ackInputSeq))) return;
  networkDeps.applyAckSeq(message.ackInputSeq);
}

function handlePong(message) {
  const id = Number(message?.id);
  const sentAt = net.pingSentAt.get(id);
  if (!Number.isFinite(sentAt)) return;
  net.pingSentAt.delete(id);
  const nowMs = Date.now();
  const sample = nowMs - sentAt;
  const delta = Math.abs(sample - net.rttMs);
  if (!Number.isFinite(net.rttMs) || net.rttMs <= 0) {
    net.rttMs = sample;
    net.jitterMs = 0;
    return;
  }
  net.rttMs = net.rttMs * 0.85 + sample * 0.15;
  net.jitterMs = net.jitterMs * 0.85 + delta * 0.15;
}

function pruneOutstandingPings(nowMs = Date.now()) {
  prunePingRecords(
    net.pingSentAt,
    nowMs,
    PING_SAMPLE_MAX_AGE_MS,
    PING_OUTSTANDING_LIMIT
  );
}

function handleChatEvent(data) {
  appendChatEntry(data, net.roomId);
}

function handleNetworkStatus(data) {
  net.playerNetworkStats.clear();
  const players = Array.isArray(data?.players) ? data.players : [];
  for (const player of players) {
    const playerId = Number(player?.playerId);
    if (!Number.isInteger(playerId) || playerId < 0 || playerId > 7) continue;
    net.playerNetworkStats.set(playerId, {
      name: typeof player?.name === 'string' ? player.name.trim().slice(0, 32) : '',
      rttMs: Math.max(0, Math.round(Number(player?.rttMs) || 0)),
      jitterMs: Math.max(0, Math.round(Number(player?.jitterMs) || 0)),
      lagging: Boolean(player?.lagging)
    });
  }
  ui.scoreDirty = true;
}

function startHeartbeat() {
  if (heartbeatHandle) clearTimeout(heartbeatHandle);
  const pulse = () => {
    if (!net.connected) {
      heartbeatHandle = 0;
      return;
    }
    networkDeps.sendCurrentInput(true);
    heartbeatHandle = setTimeout(pulse, networkDeps.inputHeartbeatIntervalMs());
  };
  heartbeatHandle = setTimeout(pulse, networkDeps.inputHeartbeatIntervalMs());
}

function startPingLoop() {
  const sendPingSample = () => {
    if (!net.connected) return;
    const pingId = net.pingSeq++;
    const sentAt = Date.now();
    pruneOutstandingPings(sentAt);
    net.pingSentAt.set(pingId, sentAt);
    net.debugStats.pingHighWater = Math.max(net.debugStats.pingHighWater, net.pingSentAt.size);
    sendPing(pingId, sentAt);
  };

  if (pingHandle) clearInterval(pingHandle);
  pingHandle = setInterval(sendPingSample, PING_INTERVAL_MS);
  sendPingSample();
}

function stopTimers() {
  if (heartbeatHandle) {
    clearTimeout(heartbeatHandle);
    heartbeatHandle = 0;
  }
  if (pingHandle) {
    clearInterval(pingHandle);
    pingHandle = 0;
  }
  networkDeps.clearAllInputRetries();
}

// Returns true when a reconnect attempt was scheduled. Backoff stays inside
// the server's mid-match disconnect grace so a resumed player keeps their curve.
function scheduleReconnect() {
  if (!net.roomId) return false;
  const stored = readSession(net.roomId);
  if (!stored) return false;
  if (reconnectState.attempts >= RECONNECT_MAX_ATTEMPTS) {
    reconnectState.attempts = 0;
    return false;
  }
  const attempt = reconnectState.attempts;
  reconnectState.attempts += 1;
  const baseDelay = Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * (2 ** attempt));
  const jitter = baseDelay * 0.2 * (Math.random() * 2 - 1);
  const delayMs = Math.max(150, Math.round(baseDelay + jitter));
  setStatus(`Reconnecting (attempt ${attempt + 1}/${RECONNECT_MAX_ATTEMPTS})...`);
  reconnectState.timer = setTimeout(() => {
    reconnectState.timer = 0;
    void attemptReconnect(stored);
  }, delayMs);
  return true;
}

async function attemptReconnect(stored) {
  if (reconnectState.suppressed || lobbyRuntime.returnToLobbyInProgress) return;
  authState.startedNetworking = true;
  try {
    await connect({
      roomId: net.roomId,
      playerName: stored.name || net.playerName,
      sessionToken: stored.token
    });
    // Success: the welcome handler resets the reconnect state.
  } catch (_) {
    authState.startedNetworking = false;
    // A failed connect() does not fire the transport 'close' event, so keep
    // driving the loop from here until the attempts run out.
    if (!scheduleReconnect()) {
      reconnectState.suppressed = false;
      if (authState.authenticated) {
        lobby.mode = 'browser';
        lobby.phase = 'waiting';
        lobby.players = [];
        lobby.message = 'Disconnected. Pick a room to rejoin.';
        setLobbyVisible(true);
        try {
          await fetchRoomDirectory();
        } catch (_) {
          // ignore
        }
        renderLobby();
      }
      resetChatFeed('', true);
      setStatus('Disconnected.');
      ui.controlsDirty = true;
    }
  }
}

function setupNetworkHandlers() {
  onNetwork('open', (data) => {
    net.connected = true;
    ui.controlsDirty = true;
    if (isLobbyVisible()) {
      setStatus('Connected');
      networkDeps.clearInput();
    } else {
      setStatus(`Connected to room "${net.roomId}"`);
    }
    startHeartbeat();
    startPingLoop();
    networkDeps.sendCurrentInput(true);
  });

  onNetwork('w', (message) => {
    handleWelcome(message);
  });

  onNetwork('s', (message) => {
    handleSnapshot(message);
  });

  onNetwork('a', (message) => {
    handleInputAck(message);
  });

  onNetwork('q', (message) => {
    handlePong(message);
  });

  onNetwork('e', (message) => {
    if (message?.kind === 'room_state') {
      if (lobbyRuntime.returnToLobbyInProgress) return;
      applyRoomState(message.data);
      return;
    }
    if (message?.kind === 'chat') {
      handleChatEvent(message.data);
      return;
    }
    if (message?.kind === 'chat_blocked') {
      showChatNotice(chatBlockedNoticeText(message?.data?.reason, message?.data?.retryInMs), net.roomId);
      return;
    }
    if (message?.kind === 'chat_deleted') {
      removeChatLineById(message?.data?.id, net.roomId);
      return;
    }
    if (message?.kind === 'chat_cleared') {
      clearChatMessages(net.roomId);
      return;
    }
    if (message?.kind === 'chat_notice') {
      if (typeof message?.data?.text === 'string') {
        showChatNotice(message.data.text, net.roomId);
      }
      return;
    }
    if (message?.kind === 'color_taken') {
      handleColorTaken(message.data);
      return;
    }
    if (message?.kind === 'trail_opacity') {
      handleTrailOpacityChanged(message.data);
      return;
    }
    if (message?.kind === 'turn_radius') {
      handleTurnRadiusChanged(message.data);
      return;
    }
    if (message?.kind === 'network_status') {
      handleNetworkStatus(message.data);
      return;
    }
    if (message?.kind === 'protocol_error') {
      setStatus(`Protocol error: ${message?.data?.reason || 'unknown'}`);
      return;
    }
    if (message?.kind === 'room_full') {
      const text = message?.data?.reason === 'room_limit'
        ? 'Room limit reached. Close one of your rooms or try again later.'
        : 'Room is full.';
      suppressReconnect(text);
      setStatus(text);
      return;
    }
    if (message?.kind === 'room_locked') {
      // The server refused the join for want of a password. The socket is
      // already closing, so the 'close' handler below puts us back in the room
      // browser -- which is where the prompt belongs, and the only place it
      // ever appears. A stored token that did not get us in is dead for this
      // room, so it goes rather than sitting there offering a rejoin.
      const roomId = message?.data?.roomId || net.roomId;
      const wrongGuess = message?.data?.reason === 'incorrect';
      suppressReconnect(wrongGuess ? 'That password did not work.' : 'This room needs a password.');
      clearSession(roomId);
      openRoomPasswordPrompt(roomId, { incorrect: wrongGuess });
      return;
    }
    if (message?.kind === 'kicked') {
      const text = 'You were kicked from the room.';
      suppressReconnect(text);
      clearSession(net.roomId);
      setStatus(text);
      return;
    }
    if (message?.kind === 'room_banned') {
      const text = 'You are banned from this room.';
      suppressReconnect(text);
      clearSession(net.roomId);
      setStatus(text);
      return;
    }
    if (message?.kind === 'session_takeover') {
      // Another tab presented our token; it owns the session now. Do not
      // reconnect from here or the two tabs would fight over the slot.
      suppressReconnect('You connected from another tab.');
      clearSession(net.roomId);
      setStatus('You connected from another tab.');
      return;
    }
    if (message?.kind === 'player_removed') {
      setStatus(`Player removed: ${message?.data?.name || 'unknown'}`);
    }
  });

  onNetwork('error', () => {
    setStatus('Network error.');
  });

  onNetwork('close', async () => {
    net.connected = false;
    clearScheduledStartSfx();
    net.pingSentAt.clear();
    net.playerNetworkStats.clear();
    ui.controlsDirty = true;
    ui.overlayDirty = true;
    authState.startedNetworking = false;
    lobbyRuntime.botActionInFlight = false;
    lobbyRuntime.matchLobbyPeek = false;
    stopTimers();
    const intentionalLobbyReturn = lobbyRuntime.returnToLobbyInProgress;
    if (!intentionalLobbyReturn && !reconnectState.suppressed && scheduleReconnect()) {
      return;
    }
    const fallbackMessage = reconnectState.fallbackMessage;
    reconnectState.suppressed = false;
    reconnectState.fallbackMessage = '';
    cancelReconnect();
    if (authState.authenticated) {
      lobby.mode = 'browser';
      lobby.phase = 'waiting';
      lobby.connectedPlayers = 0;
      lobby.connectedClients = 0;
      lobby.humanPlayers = 0;
      lobby.botPlayers = 0;
      lobby.manualBotCount = 0;
      lobby.players = [];
      lobby.iAmSpectator = false;
      lobby.iAmMuted = false;
      lobby.message = fallbackMessage
        || (intentionalLobbyReturn ? 'Create or join a room.' : 'Disconnected. Pick a room to rejoin.');
      setLobbyVisible(true);
      try {
        await fetchRoomDirectory();
      } catch (_) {
        // ignore
      }
      renderLobby();
    }
    resetChatFeed('', true);
    setStatus(fallbackMessage || (intentionalLobbyReturn ? `Signed in as ${net.playerName}` : 'Disconnected.'));
    lobbyRuntime.returnToLobbyInProgress = false;
    ui.controlsDirty = true;
  });
}

async function startNetworking({
  playerNameOverride = null,
  roomIdOverride = null,
  suppressLobbyOverlay = false,
  roomPassword = ''
} = {}) {
  lobbyRuntime.returnToLobbyInProgress = false;
  if (authState.startedNetworking) return;
  const params = new URLSearchParams(window.location.search);
  const roomId = roomIdOverride || params.get('room') || 'default';
  const playerName = playerNameOverride || params.get('name') || net.playerName;
  authState.startedNetworking = true;
  clearScheduledStartSfx();
  ui.hasStateSnapshot = false;
  ui.overlayStateSig = '';
  ui.scoreSig = '';
  ui.standingsSig = '';
  ui.waitingRoundKey = '';
  ui.waitingOverlayNeedsAnimate = false;
  ui.scoreDirty = true;
  ui.overlayDirty = true;
  ui.controlsDirty = true;
  ui.lastOverlayCountdown = -1;
  lobbyRuntime.botActionInFlight = false;
  net.roomId = roomId;
  net.playerName = playerName;
  lobby.mode = 'room';
  lobby.roomId = roomId;
  lobby.roomName = roomId.toUpperCase();
  lobby.connectedPlayers = 0;
  lobby.connectedClients = 0;
  lobby.humanPlayers = 0;
  lobby.botPlayers = 0;
  lobby.manualBotCount = 0;
  lobby.hostName = '';
  lobby.isHost = false;
  lobby.startMode = 'host';
  lobby.phase = 'waiting';
  lobby.canStart = false;
  lobby.players = [];
  lobby.message = 'Joining room...';
  resetChatFeed(roomId, true);
  setLobbyCopyButtonLabel('Copy Invite');
  if (suppressLobbyOverlay) {
    setLobbyVisible(false);
  } else {
    setLobbyVisible(true);
    renderLobby();
  }
  setStatus(`Connecting to room "${roomId}"...`);
  const storedSession = readSession(roomId);
  try {
    await connect({
      roomId,
      playerName: storedSession?.name || playerName,
      sessionToken: storedSession?.token,
      // Held only for the duration of this join. A stored session token gets
      // in on its own, so this is empty for every rejoin; when the token has
      // lapsed the server answers 'room_locked' and the prompt comes back.
      password: roomPassword
    });
  } catch (error) {
    authState.startedNetworking = false;
    if (authState.authenticated) {
      lobby.mode = 'browser';
      lobby.message = 'Could not join room. Choose another room.';
      setLobbyVisible(true);
      try {
        await fetchRoomDirectory();
      } catch (_) {
        // ignore
      }
      renderLobby();
    }
    setStatus(`Connect failed: ${error?.message || 'unknown error'}`);
  }
}

export {
  setNetworkDeps,
  setReplayFrameRecorder,
  pruneOutstandingPings,
  setupNetworkHandlers,
  startNetworking,
  stopTimers
};
